import { renderTextWithMath } from './MathRenderer';
import { ImageBlock } from './ImageBlock';
import { AudioPlayer } from './AudioPlayer';
import { CodeBlock } from './CodeBlock';

interface TextBlockProps {
  text: string;
  className?: string;
  inline?: boolean;
}

// Matches a markdown fenced code block: ```lang\n ... \n```
// Language is optional; content is captured non-greedily up to the closing fence.
const FENCE_RE = /```[ \t]*([A-Za-z0-9+#._-]*)[ \t]*\r?\n?([\s\S]*?)```/g;

function hasRichMedia(text: string): boolean {
  return /!\[.*?\]\(.*?\)/.test(text) || /🔊\[.*?\]\(.*?\)/.test(text);
}

function renderRichSegments(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /!\[(.*?)\]\((.*?)\)|🔊\[(.*?)\]\((.*?)\)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore.includes('$')) {
        parts.push(...renderTextWithMath(textBefore).map((node) =>
          <span key={`t${key++}`}>{node}</span>
        ));
      } else {
        parts.push(<span key={`t${key++}`}>{textBefore}</span>);
      }
    }

    if (match[1] !== undefined && match[2] !== undefined) {
      parts.push(<ImageBlock key={`img${key++}`} src={match[2]} alt={match[1]} />);
    } else if (match[3] !== undefined && match[4] !== undefined) {
      parts.push(<AudioPlayer key={`aud${key++}`} src={match[4]} />);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex);
    if (remaining.includes('$')) {
      parts.push(...renderTextWithMath(remaining).map((node) =>
        <span key={`t${key++}`}>{node}</span>
      ));
    } else {
      parts.push(<span key={`t${key++}`}>{remaining}</span>);
    }
  }

  return parts;
}

// Renders a plain text segment (no fenced code) into paragraph blocks,
// applying inline media and math handling per line.
function renderParagraphs(text: string): React.ReactNode {
  const paragraphs = text.split('\n').filter((line) => line.trim().length > 0);
  return paragraphs.map((p, i) => (
    <div key={i} className={i > 0 ? 'mt-2' : ''}>
      {hasRichMedia(p)
        ? renderRichSegments(p)
        : p.includes('$')
          ? renderTextWithMath(p)
          : p}
    </div>
  ));
}

export function TextBlock({ text, className = '', inline = false }: TextBlockProps) {
  if (inline) {
    const hasMath = text.includes('$');
    const hasMedia = hasRichMedia(text);
    return (
      <span className={`text-fg-muted ${className}`}>
        {hasMedia
          ? renderRichSegments(text)
          : hasMath
            ? renderTextWithMath(text)
            : text}
      </span>
    );
  }

  // Extract fenced code blocks and render them with the CodeBlock component,
  // keeping the surrounding prose as normal paragraphs.
  if (text.includes('```')) {
    const segments: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    FENCE_RE.lastIndex = 0;

    while ((match = FENCE_RE.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const before = text.slice(lastIndex, match.index);
        if (before.trim()) {
          segments.push(<div key={`p${key++}`}>{renderParagraphs(before)}</div>);
        }
      }

      const language = (match[1] || 'text').toLowerCase();
      const code = match[2].replace(/\n+$/, '');
      segments.push(<CodeBlock key={`c${key++}`} code={code} language={language} />);

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      const after = text.slice(lastIndex);
      if (after.trim()) {
        segments.push(<div key={`p${key++}`}>{renderParagraphs(after)}</div>);
      }
    }

    return (
      <div className={`text-fg-muted text-sm sm:text-base leading-relaxed space-y-3 ${className}`}>
        {segments}
      </div>
    );
  }

  return (
    <div className={`text-fg-muted text-sm sm:text-base leading-relaxed ${className}`}>
      {renderParagraphs(text)}
    </div>
  );
}
